// ./src/utils/api/PartnerPaymentService.ts
//
// Partner payments — a focused wrapper over the single money `payment` ledger,
// scoped to one event partner via eventPartnerId. A sponsor/vendor may pay in
// several partial payments across methods (online / POS / cheque / cash / other).
// The ledger is APPEND-ONLY (no remove) — a mistaken entry is voided by setting
// paymentStatus = 'canceled'. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants — the ledger's `processor` is the payment method. Friendly labels
// map onto the eventsmagic-api PAYMENT_PROCESSOR boundary values.
// =============================================================================

export const PARTNER_PAYMENT_METHOD = {
  ONLINE: "stripe",
  POS: "pos",
  CHEQUE: "cheque",
  CASH: "manual",
  OTHER: "other",
} as const;

export type PartnerPaymentMethod = (typeof PARTNER_PAYMENT_METHOD)[keyof typeof PARTNER_PAYMENT_METHOD];

export const PARTNER_PAYMENT_METHOD_VALUES: PartnerPaymentMethod[] = Object.values(PARTNER_PAYMENT_METHOD);

export const PARTNER_PAYMENT_METHOD_LABELS: Record<PartnerPaymentMethod, string> = {
  stripe: "Online (platform)",
  pos: "Card terminal (POS)",
  cheque: "Cheque",
  manual: "Cash",
  other: "Other",
};

export const partnerPaymentMethodLabel = (processor?: string | null): string =>
  PARTNER_PAYMENT_METHOD_LABELS[processor as PartnerPaymentMethod] ?? processor ?? "";

// Settlement state of the charge (subset of PAYMENT_STATUS relevant to manual entry).
export const PARTNER_PAYMENT_STATUS = {
  SUCCEEDED: "succeeded",
  PENDING: "pending",
  CANCELED: "canceled",
  REFUNDED: "refunded",
} as const;

export type PartnerPaymentStatus = (typeof PARTNER_PAYMENT_STATUS)[keyof typeof PARTNER_PAYMENT_STATUS];

export const PARTNER_PAYMENT_STATUS_LABELS: Record<string, string> = {
  succeeded: "Received",
  pending: "Pending",
  canceled: "Voided",
  refunded: "Refunded",
  partially_refunded: "Partially refunded",
  failed: "Failed",
};

export const partnerPaymentStatusLabel = (status?: string | null): string =>
  PARTNER_PAYMENT_STATUS_LABELS[status ?? ""] ?? status ?? "";

// =============================================================================
// GraphQL
// =============================================================================

const PARTNER_PAYMENT_FIELDS = `
  id
  eventPartnerId
  eventId
  paymentType
  direction
  isTest
  isRefund
  grossAmount
  serviceFeeAmount
  netAmount
  refundedAmount
  currencyCode
  processor
  processorPaymentId
  processorAccountId
  partnerApplicationId
  payerEmail
  referenceCode
  paymentStatus
  paidAt
  note
  personId
  status
  createdAt
  updatedAt
`;

const PARTNER_PAYMENT_LIST_QUERY = gql`
  query PartnerPaymentList($filter: PaymentFilter, $params: PaginationAndSorting) {
    paymentList(filter: $filter, params: $params) {
      code
      data { ${PARTNER_PAYMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PARTNER_PAYMENT_GET_QUERY = gql`
  query PartnerPaymentGet($id: ID) {
    paymentGet(id: $id) {
      code
      data { ${PARTNER_PAYMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PARTNER_PAYMENT_CREATE_MUTATION = gql`
  mutation PartnerPaymentCreate($params: PaymentInput) {
    paymentCreate(params: $params) {
      code
      data { ${PARTNER_PAYMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PARTNER_PAYMENT_UPDATE_MUTATION = gql`
  mutation PartnerPaymentUpdate($where: PaymentWhereInput, $params: PaymentInput) {
    paymentUpdate(where: $where, params: $params) {
      code
      data { ${PARTNER_PAYMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;


/**
 * Refund one partner payment — a platform charge through Stripe, or a hand-recorded one RECORDED as
 * returned (nothing is sent; owner, 2026-09-18).
 *
 * ⚠ `alsoWithdraw` IS REQUIRED, and the type says so. It decides whether the sponsor's place comes
 * back and whether they disappear from the event website; the server refuses the call when it is
 * absent rather than reading it as false, so an optional field here would only move the failure.
 */
const PARTNER_PAYMENT_REFUND_MUTATION = gql`
  mutation PartnerPaymentRefund($params: PartnerPaymentRefundInput) {
    partnerPaymentRefund(params: $params) {
      code
      data { id applicationStatus paymentStatus }
      errors { name errors warnings }
    }
  }
`;

export interface PartnerPaymentRefundInput {
  /** The ledger row to refund. A platform charge goes back through Stripe; a hand-recorded one is recorded. */
  paymentId: string;
  /** How much to send back. Omit / null for everything still outstanding. */
  amount?: number | null;
  /** ⚠ Required: true also releases the place and removes the partner record. Refused with no application. */
  alsoWithdraw: boolean;
  /** Why — kept on the refund row. */
  note?: string | null;
}

// =============================================================================
// Types (subset of the Payment ledger)
// =============================================================================

export interface PartnerPayment {
  id?: string;
  eventPartnerId?: string | null;
  eventId?: string | null;
  paymentType?: string | null;
  direction?: string | null;
  isTest?: boolean | null;
  /** A refund of an earlier charge. ⚠ It SUBTRACTS from what the host has been paid. */
  isRefund?: boolean | null;
  /** ⚠ What the PAYER was charged. Not what the host is owed — see `netAmount`. */
  grossAmount?: number | null;
  /** OUR platform service fee inside `grossAmount`, when the host passed it on. */
  serviceFeeAmount?: number | null;
  /**
   * ⚠ WHAT THE HOST ACTUALLY RECEIVES, and therefore the only thing that settles the agreed amount.
   * A manual entry has net === gross; a platform charge does not, and summing gross against an
   * agreed 500 reported a balance of −7.50 on a sponsorship that was paid to the penny.
   */
  netAmount?: number | null;
  /** How much of this charge has ALREADY been sent back — what caps the next refund. */
  refundedAmount?: number | null;
  currencyCode?: string | null;
  /** The payment method, stored as the ledger's processor. */
  processor?: string | null;
  /** The Stripe PaymentIntent / refund id, when a processor took it. */
  processorPaymentId?: string | null;
  /** The host's CONNECTED account — without it a Stripe link points at OUR dashboard. */
  processorAccountId?: string | null;
  /** The sponsorship application this settles, which is what a receipt is printed from. */
  partnerApplicationId?: string | null;
  /** The APPLICANT's email on a payment made through an application — who receipts and refund notices go to. */
  payerEmail?: string | null;
  referenceCode?: string | null;
  paymentStatus?: string | null;
  paidAt?: string | null;
  note?: string | null;
  personId?: string | null;
  status?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface PartnerPaymentInput {
  eventPartnerId?: string | null;
  eventId?: string | null;
  paymentType?: string | null;
  grossAmount?: number | null;
  netAmount?: number | null;
  currencyCode?: string | null;
  processor?: string | null;
  paymentStatus?: string | null;
  paidAt?: string | null;
  note?: string | null;
  personId?: string | null;
}

export interface PartnerPaymentFilter {
  eventPartnerId?: string[] | null;
  eventId?: string[] | null;
  paymentType?: string[] | null;
  paymentStatus?: string[] | null;
}

// =============================================================================
// Service
// =============================================================================

export class PartnerPaymentService {
  static async list(
    filter?: PartnerPaymentFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(PARTNER_PAYMENT_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: PartnerPaymentFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<PartnerPayment>(PartnerPaymentService.list, filter, sortBy, opts);
  }

  /** One ledger row by id — for a URL-stage drawer that must open on a cold reload. */
  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(PARTNER_PAYMENT_GET_QUERY, { id });
  }

  static async create(params: PartnerPaymentInput): Promise<OpResult> {
    // Sponsorship / vendor money always flows to the host; the server stamps
    // direction = host_revenue from the type. net = gross for manual entries.
    return ApiService.executeMutation(PARTNER_PAYMENT_CREATE_MUTATION, {
      params: { paymentType: "sponsorship", ...params },
    });
  }

  static async update(id: string, params: PartnerPaymentInput): Promise<OpResult> {
    return ApiService.executeMutation(PARTNER_PAYMENT_UPDATE_MUTATION, { where: { id }, params });
  }

  /** Void a mistaken entry (the ledger is append-only — no hard delete). */
  static async void(id: string): Promise<OpResult> {
    return ApiService.executeMutation(PARTNER_PAYMENT_UPDATE_MUTATION, {
      where: { id },
      params: { paymentStatus: PARTNER_PAYMENT_STATUS.CANCELED },
    });
  }
  /**
   * Refund a partner payment (platform charge → Stripe; hand-recorded → recorded only).
   *
   * ⚠ NOT an edit of the ledger row. The row records what a processor actually did; a refund is a
   * NEW movement, and the server writes it as its own row plus the charge's new settled state. That
   * is why this is a dedicated mutation and not `paymentUpdate`.
   */
  static async refund(params: PartnerPaymentRefundInput): Promise<OpResult> {
    return ApiService.executeMutation(PARTNER_PAYMENT_REFUND_MUTATION, { params });
  }
}

// The pure partner money maths lives in its own module so a node guard (`check-ledger`) can RUN it —
// this file imports the API client, which a script cannot load. Re-exported so callers are unchanged.
export * from "../partnerMoney";
